import { useEffect, useMemo, useState } from 'react';
import { BehaviorSubject } from 'rxjs';

// Observable state for API license lock (only header is stored)
interface ApiLicenseState {
  header: string | null;
}

const apiLicenseLockSubject = new BehaviorSubject<ApiLicenseState>({
  header: null,
});

// Function to update the license lock status based on header value
export const updateApiLicenseLock = (apiLicenseHeader: string | null) => {
  apiLicenseLockSubject.next({ header: apiLicenseHeader || null });
};

// Hook to use the API license lock state
export const useApiLicenseLock = () => {
  const [state, setState] = useState<ApiLicenseState>({ header: null });

  useEffect(() => {
    const subscription = apiLicenseLockSubject.subscribe((newState) => {
      setState((prevState) => {
        // Only update if header actually changed
        if (prevState.header !== newState.header) {
          return newState;
        }
        return prevState;
      });
    });
    return () => subscription.unsubscribe();
  }, []);

  // Memoized deconstructed state
  const memoizedState = useMemo(() => {
    const header = state.header;
    let current: number | null = null;
    let max: number | null = null;
    let locked = true;

    if (header) {
      const parts = header.split('/');
      if (parts.length >= 2) {
        current = Number(parts[0]);
        max = Number(parts[1]);
        if (!isNaN(current) && !isNaN(max)) {
          locked = current > max;
        }
      }
    } else {
      // If no header, not locked
      locked = false;
    }

    return { locked, header, current, max };
  }, [state]);

  return memoizedState;
};
// Export the subject for use in Apollo link
export { apiLicenseLockSubject };
